import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldAlert, RefreshCw, Loader2 } from "lucide-react";

const RECORD_ROUTE = {
  tasks: (id) => `/tasks/${id}`,
  installations: (id) => `/tasks/${id}`,
  customers: (id) => `/users/${id}`,
  products: (id) => `/products/${id}`,
  quotes: (id) => `/quotes/${id}`,
};

const STATUS_STYLE = {
  THREATS_FOUND: "bg-rose-100 text-rose-700",
  ACCESS_DENIED: "bg-amber-100 text-amber-700",
  FAILED: "bg-slate-200 text-slate-700",
  UNSUPPORTED: "bg-slate-200 text-slate-700",
};

const fmt = (t) => (t ? new Date(t).toLocaleString("en-GB") : "—");

export default function QuarantinePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attachments/quarantine");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 lg:p-8" data-testid="quarantine-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-rose-500" /> Quarantine
          </h1>
          <p className="text-sm text-slate-500 mt-1">Attachments blocked by GuardDuty malware scanning. These files can never be downloaded.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50" data-testid="quarantine-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="quarantine-empty">
          No blocked or flagged files. All scanned attachments are clean. ✅
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="quarantine-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3">Linked record</th>
                <th className="px-4 py-3">Uploaded by</th>
                <th className="px-4 py-3">Scan result</th>
                <th className="px-4 py-3">Uploaded</th>
                <th className="px-4 py-3">Scanned</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => {
                const route = RECORD_ROUTE[a.record_type]?.(a.record_id);
                return (
                  <tr key={a.id} className="border-b border-slate-50 last:border-0" data-testid={`quarantine-row-${a.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{a.filename}<div className="text-[11px] text-slate-400">{a.mime} · {((a.size || 0) / 1024).toFixed(0)} KB</div></td>
                    <td className="px-4 py-3">
                      {route ? <Link to={route} className="text-indigo-600 hover:underline">{a.record_type}/{a.record_id}</Link>
                             : <span className="text-slate-600">{a.record_type}/{a.record_id}</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{a.uploaded_by}</td>
                    <td className="px-4 py-3"><span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[a.scan_status] || "bg-slate-200 text-slate-700"}`} data-testid={`quarantine-status-${a.id}`}>{a.scan_status}</span></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmt(a.completed_at || a.created_at)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmt(a.scan_completed_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
